import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { votesApiService } from '../services/votesApi';
import { prefetchPlayerUuids, getCachedUuid } from '../hooks/usePlayerFromUsername';
import { PlayerProfile } from '../components/PlayerProfile';
import { VoteChart } from '../components/VoteChart';
import { VoteUserDrilldownModal } from '../components/VoteUserDrilldownModal';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import {
  VotePeriod,
  VoteStats,
  VoteLeaderboardEntry,
  VoteServiceInfo,
  VoteHistoryResponse,
} from '../types/vote';
import {
  BarChart3,
  Users,
  Globe,
  TrendingUp,
  Trophy,
  Award,
  Vote,
  ChevronRight,
  Loader,
  AlertCircle,
  RefreshCw,
  Crown,
  Medal,
  X,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { motion, AnimatePresence } from 'framer-motion';

const PERIOD_OPTIONS: { value: VotePeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: '3months', label: '3 Months' },
  { value: 'all', label: 'All Time' },
];

const LEADERBOARD_LIMIT = 25;

/** Pick the best granularity for a given period — use hourly for short ranges where we have fine data */
const granularityForPeriod = (period: VotePeriod): string => {
  switch (period) {
    case 'today':
    case 'week':
      return 'hour';
    default:
      return 'day';
  }
};

// --- Sub-components ---

const PeriodSelector = ({ selected, onChange }: { selected: VotePeriod; onChange: (p: VotePeriod) => void }) => (
  <div className="segmented" aria-label="Period">
    {PERIOD_OPTIONS.map(opt => (
      <button
        key={opt.value}
        type="button"
        aria-pressed={selected === opt.value}
        data-active={selected === opt.value}
        onClick={() => onChange(opt.value)}
        className="segmented-item flex-shrink-0"
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const LeaderboardEntry = ({ entry, rank, onClick }: {
  entry: VoteLeaderboardEntry;
  rank: number;
  onClick: () => void;
}) => {
  const [uuid, setUuid] = useState<string | null>(getCachedUuid(entry.username));

  useEffect(() => {
    const handleUpdate = () => {
      const resolved = getCachedUuid(entry.username);
      if (resolved) setUuid(resolved);
    };
    document.addEventListener('playerUuidsUpdated', handleUpdate);
    handleUpdate();
    return () => document.removeEventListener('playerUuidsUpdated', handleUpdate);
  }, [entry.username]);

  const getRankBadge = (rank: number) => {
    if (rank === 1) return <Crown className="w-4 h-4 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-4 h-4 text-gray-300" />;
    if (rank === 3) return <Medal className="w-4 h-4 text-amber-600" />;
    return (
      <span className="w-5 h-5 flex items-center justify-center text-xs font-semibold tabular-nums text-glass-400">
        {rank}
      </span>
    );
  };

  return (
    <motion.button
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.03 }}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-glass-200/5 transition-colors group text-left"
      title={`View ${entry.username}'s vote breakdown`}
    >
      <div className="flex-shrink-0 w-8 flex justify-center">
        {getRankBadge(rank)}
      </div>
      <div className="flex-1 min-w-0">
        {uuid ? (
          <PlayerProfile uuid={uuid} name={entry.username} showUuid={false} showAvatar={true} inline={true} />
        ) : (
          <span className="text-white font-medium text-sm">{entry.username}</span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-white font-semibold tabular-nums">{(entry.vote_count ?? 0).toLocaleString()}</span>
        <ChevronRight className="w-4 h-4 text-glass-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </motion.button>
  );
};

const ServiceBreakdownBar = ({ service, maxVotes, rank, onClick }: {
  service: VoteServiceInfo;
  maxVotes: number;
  rank: number;
  onClick: () => void;
}) => {
  const votes = service.vote_count ?? 0;
  const widthPercent = maxVotes > 0 ? (votes / maxVotes) * 100 : 0;

  const barColors = [
    'bg-accent-400',
    'bg-blue-400',
    'bg-emerald-400',
    'bg-orange-400',
    'bg-pink-400',
    'bg-indigo-400',
    'bg-teal-400',
    'bg-amber-400',
  ];

  return (
    <motion.button
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.05 }}
      onClick={onClick}
      className="w-full text-left space-y-1.5 group hover:bg-glass-200/5 px-2 py-2 rounded-lg transition-colors"
      title={`Show top voters for ${service.service_name}`}
    >
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Globe className="w-4 h-4 text-accent-400 flex-shrink-0" />
          <span className="text-white font-medium truncate group-hover:text-accent-300 transition-colors">{service.service_name}</span>
        </div>
        <span className="text-glass-300 font-mono text-xs tabular-nums ml-2 flex-shrink-0">{votes.toLocaleString()}</span>
      </div>
      <div className="w-full h-2 bg-glass-200/10 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${widthPercent}%` }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: rank * 0.05 }}
          className={cn("h-full rounded-full", barColors[rank % barColors.length])}
        />
      </div>
    </motion.button>
  );
};

// --- Main View ---

export const VotesView = () => {
  useDocumentTitle('Votes');

  const [period, setPeriod] = useState<VotePeriod>('month');
  const [stats, setStats] = useState<VoteStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<VoteLeaderboardEntry[]>([]);
  const [services, setServices] = useState<VoteServiceInfo[]>([]);
  const [history, setHistory] = useState<VoteHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUsername, setSelectedUsername] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [statsData, leaderboardData, servicesData, historyData] = await Promise.all([
        votesApiService.getStats(period),
        votesApiService.getLeaderboard(period, LEADERBOARD_LIMIT),
        votesApiService.getServices(period),
        votesApiService.getHistory(period, granularityForPeriod(period)),
      ]);

      setStats(statsData);
      setLeaderboard(leaderboardData);
      setServices(servicesData);
      setHistory(historyData);

      // Batch resolve all leaderboard usernames to UUIDs for PlayerProfile display
      if (leaderboardData.length > 0) {
        prefetchPlayerUuids(leaderboardData.map(e => e.username));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vote data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sortedServices = useMemo(() => {
    return [...services].sort((a, b) => (b.vote_count ?? 0) - (a.vote_count ?? 0));
  }, [services]);

  const maxServiceVotes = useMemo(() => {
    if (sortedServices.length === 0) return 0;
    return sortedServices[0]?.vote_count ?? 0;
  }, [sortedServices]);

  const avgVotesPerDay = useMemo(() => {
    if (!history?.data?.length) return '0';
    const total = history.data.reduce((sum, d) => sum + (d.vote_count ?? 0), 0);
    const days = history.data.length;
    return days > 0 ? (total / days).toFixed(1) : '0';
  }, [history]);

  const periodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label;

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <PageHeader
        eyebrow="Players"
        title="Vote Tracking"
        description="Track player votes across all vote listing sites"
        icon={Vote}
        actions={
          <button
            onClick={fetchData}
            disabled={loading}
            className="glass-button"
            title="Refresh data"
            aria-label="Refresh data"
          >
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            Refresh
          </button>
        }
      >
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <PeriodSelector selected={period} onChange={setPeriod} />
          {!loading && !error && (
            <span className="text-xs text-glass-400 tabular-nums md:ml-auto">
              {periodLabel} · {leaderboard.length.toLocaleString()} voters shown
            </span>
          )}
        </div>
      </PageHeader>

      {/* Error state */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="alert alert-danger"
          role="alert"
        >
          <AlertCircle />
          <span className="flex-1 min-w-0">{error}</span>
          <button onClick={fetchData} className="glass-button-secondary btn-xs shrink-0">
            Retry
          </button>
        </motion.div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Total Votes"
          value={stats?.total_votes?.toLocaleString() || '0'}
          icon={BarChart3}
          tone="accent"
          loading={loading}
        />
        <StatTile
          label="Unique Voters"
          value={stats?.unique_voters?.toLocaleString() || '0'}
          icon={Users}
          tone="info"
          loading={loading}
        />
        <StatTile
          label="Vote Sites"
          value={stats?.active_services?.toLocaleString() || '0'}
          icon={Globe}
          tone="success"
          loading={loading}
        />
        <StatTile
          label="Avg / Day"
          value={avgVotesPerDay}
          hint="votes per day"
          icon={TrendingUp}
          tone="warning"
          loading={loading}
        />
      </div>

      {/* Daily vote chart */}
      <VoteChart
        data={history?.data || []}
        title="Daily Vote History"
        height={320}
        isLoading={loading}
        emptyMessage="No vote history available for this period."
      />

      {/* Leaderboard + Service Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Leaderboard */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader
            title="Top Voters"
            icon={Trophy}
            iconClassName="text-yellow-400"
            as="h2"
            actions={
              <span className="text-xs text-glass-400">
                {periodLabel}
              </span>
            }
          />

          {loading ? (
            <div className="space-y-1" aria-busy="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-2 py-2.5">
                  <SkeletonLoader className="w-5 h-5 rounded-full" />
                  <SkeletonLoader className="w-7 h-7 rounded-md" />
                  <SkeletonLoader className="h-4 flex-1" />
                  <SkeletonLoader className="h-4 w-10" />
                </div>
              ))}
            </div>
          ) : leaderboard.length === 0 ? (
            <EmptyState bare compact icon={Award} title="No votes recorded" description="No votes recorded for this period." />
          ) : (
            <div className="divide-y divide-glass-200/10 max-h-[600px] overflow-y-auto pr-1">
              {leaderboard.map((entry, index) => (
                <LeaderboardEntry
                  key={entry.username}
                  entry={entry}
                  rank={index + 1}
                  onClick={() => setSelectedUsername(entry.username)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Service breakdown */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader
            title="Vote Sites"
            icon={Globe}
            as="h2"
            actions={
              <span className="text-xs text-glass-400 tabular-nums">
                {sortedServices.length} active
              </span>
            }
          />

          {loading ? (
            <div className="space-y-2" aria-busy="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="space-y-2 px-2 py-2">
                  <div className="flex justify-between">
                    <SkeletonLoader className="h-4 w-40" />
                    <SkeletonLoader className="h-4 w-12" />
                  </div>
                  <SkeletonLoader className="h-2 w-full rounded-full" />
                </div>
              ))}
            </div>
          ) : sortedServices.length === 0 ? (
            <EmptyState bare compact icon={Globe} title="No vote sites" description="No vote services found for this period." />
          ) : (
            <div className="space-y-1 max-h-[600px] overflow-y-auto pr-1">
              {sortedServices.map((service, index) => (
                <ServiceBreakdownBar
                  key={service.service_name}
                  service={service}
                  maxVotes={maxServiceVotes}
                  rank={index}
                  onClick={() => setSelectedService(
                    selectedService === service.service_name ? null : service.service_name
                  )}
                />
              ))}

              {/* Total votes summary */}
              <div className="mt-3 pt-3 border-t border-glass-200/10 flex items-center justify-between text-sm px-2">
                <span className="text-glass-400">Total across all sites</span>
                <span className="text-white font-semibold tabular-nums">
                  {sortedServices.reduce((sum, s) => sum + (s.vote_count ?? 0), 0).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Service detail chart (shown when a service is selected) */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="glass-card p-4 md:p-5">
              <SectionHeader
                title={`${selectedService} — Top Voters`}
                icon={Globe}
                actions={
                  <button
                    onClick={() => setSelectedService(null)}
                    className="glass-button-secondary btn-xs"
                    title="Close service detail"
                  >
                    <X className="w-3.5 h-3.5" />
                    Close
                  </button>
                }
              />
              <ServiceDetailPanel serviceName={selectedService} period={period} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* User drilldown modal */}
      {selectedUsername && (
        <VoteUserDrilldownModal
          username={selectedUsername}
          period={period}
          isOpen={!!selectedUsername}
          onClose={() => setSelectedUsername(null)}
        />
      )}
    </div>
  );
};

// Panel that loads and displays service detail (top voters for a service)
const ServiceDetailPanel = ({ serviceName, period }: { serviceName: string; period: VotePeriod }) => {
  const [data, setData] = useState<{ service_name: string; total_votes: number; top_voters: VoteLeaderboardEntry[] } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    votesApiService.getByService(serviceName, period)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [serviceName, period]);

  useEffect(() => {
    if (data?.top_voters?.length) {
      prefetchPlayerUuids(data.top_voters.map(v => v.username));
    }
  }, [data]);

  if (loading) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="subcard p-3 flex items-center gap-3">
              <SkeletonLoader className="w-5 h-4" />
              <SkeletonLoader className="w-7 h-7 rounded-md" />
              <SkeletonLoader className="h-4 flex-1" />
              <SkeletonLoader className="h-4 w-8" />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center py-2">
          <Loader className="w-5 h-5 animate-spin text-accent-400" />
        </div>
      </div>
    );
  }

  if (!data || !data.top_voters?.length) {
    return <EmptyState bare compact icon={Users} title="No voter data" description="No voter data available for this service." />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {data.top_voters.map((voter, i) => (
        <ServiceVoterCard key={voter.username} voter={voter} rank={i + 1} />
      ))}
    </div>
  );
};

const ServiceVoterCard = ({ voter, rank }: { voter: VoteLeaderboardEntry; rank: number }) => {
  const [uuid, setUuid] = useState<string | null>(getCachedUuid(voter.username));

  useEffect(() => {
    const handleUpdate = () => {
      const resolved = getCachedUuid(voter.username);
      if (resolved) setUuid(resolved);
    };
    document.addEventListener('playerUuidsUpdated', handleUpdate);
    handleUpdate();
    return () => document.removeEventListener('playerUuidsUpdated', handleUpdate);
  }, [voter.username]);

  return (
    <div className="subcard p-3 flex items-center gap-3">
      <span className="text-xs font-semibold tabular-nums text-glass-400 w-6 text-center shrink-0">{rank}</span>
      <div className="flex-1 min-w-0">
        {uuid ? (
          <PlayerProfile uuid={uuid} name={voter.username} showUuid={false} showAvatar={true} inline={true} />
        ) : (
          <span className="text-white font-medium text-sm">{voter.username}</span>
        )}
      </div>
      <span className="text-white font-semibold tabular-nums">{(voter.vote_count ?? 0).toLocaleString()}</span>
    </div>
  );
};
